import ScreenLayout from 'components/common/ScreenLayout';
import React, {useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {colors} from 'styles/colors';
import {minmax} from 'utils/minmax';
import DefaultText from 'components/common/DefaultText';
import {useRecoilValue} from 'recoil';
import {userInfoState} from 'utils/Atoms';
import {deleteStatus, getStatusList} from 'function/api';
import {IStatus, ResponseGetStatusList} from 'interfaces/interface';
import DefaultButton from 'components/common/DefaultButton';
import RegisterNoticeModal from 'components/notice/RegisterNoticeModal';
import NoticeItem from 'components/notice/NoticeItem';
import CommonModal from 'components/common/CommonModal';
import useInfiniteScrolling from 'utils/useInfiniteScrolling';
import {useOutSideRef} from 'utils/useOutSideRef';

/**
 * 공지사항 페이지 컴포넌트
 * 일반 공지 및 서버 시스템 상태를 관리하는 페이지입니다.
 * @returns {JSX.Element} 공지사항 페이지 컴포넌트
 */
const Notice = () => {
  // locale
  const [isLoading, setIsLoading] = useState(false);
  const [errorModal, setErrorModal] = useState({});

  const userInfo = useRecoilValue(userInfoState);

  const [isNormalNotice, setIsNormalNotice] = useState(false);
  const [noticeData, setNoticeData] = useState<ResponseGetStatusList | null>(
    null,
  );
  const [selectParentNotice, setSelectParentNotice] = useState<null | IStatus>(
    null,
  );

  const [modal, setModal] = useState('');

  // 무한 스크롤
  const listRef = useRef(null);
  const [observerRef, setObserverRef] = useState<null | HTMLDivElement>(null);
  // 스크롤 정보
  const [scrollData, setScrollData] = useState({
    page: 1,
    list: [],
    lastOfList: false,
    noResults: false,
  });
  /**
   * 무한 스크롤을 위한 데이터 추가 조회 함수
   * 기존 목록에 추가 데이터를 불러옵니다.
   * @returns {Promise<void>}
   */
  const fetchBoxList = React.useCallback(async () => {
    await getList(true);
  }, [scrollData.page]);
  // 무한 스크롤 함수
  useInfiniteScrolling({
    observerRef,
    fetchMore: fetchBoxList,
    hasMore: !scrollData.lastOfList && !scrollData.noResults,
  });

  /**
   * 공지사항 목록을 조회합니다.
   * 무한 스크롤을 지원하며, 기존 데이터에 추가하거나 새로 설정합니다.
   * @param {boolean} [isFetch] - 추가 조회 여부 (무한 스크롤용)
   * @returns {Promise<void>}
   */
  const getList = async (isFetch?: boolean) => {
    const result = await getStatusList({
      userInfo,
      setIsLoading,
      setErrorModal,
      isNormalNotice,
      page: scrollData.page,
    });
    if (result) {
      setNoticeData({
        ...result,
        statuses:
          isFetch && noticeData
            ? [...noticeData.statuses, ...result.statuses]
            : result.statuses,
      });
      setScrollData(
        result.statuses.length < 20
          ? {...scrollData, lastOfList: true}
          : {...scrollData, page: scrollData.page + 1},
      );
    }
  };

  useEffect(() => {
    getList();
  }, [isNormalNotice]);

  const modalData: {[key: string]: any} = {
    // 본사시스템 서비스 준비중
    completeDelete: {
      title: '삭제 성공!',
      detail: '공지 삭제를 완료했습니다.',
      firstButtonText: 'confirm',
      firstButtonClick: () => {
        setModal('');
        window.location.reload();
      },
    },
  };

  /**
   * 공지사항 버튼 클릭 핸들러
   * 등록, 수정, 삭제 작업을 처리합니다.
   * @param {IStatus} item - 공지사항 항목
   * @param {'register' | 'modify' | 'delete'} type - 작업 타입
   * @returns {Promise<void>}
   */
  const handleClickButtons = async (
    item: IStatus,
    type: 'register' | 'modify' | 'delete',
  ) => {
    if (type === 'delete') {
      if (window.confirm('삭제하시겠습니까?') && item.uuid) {
        const result = await deleteStatus({
          userInfo,
          setIsLoading,
          setErrorModal,
          statusUUID: item.uuid,
        });
        if (result) setModal('completeDelete');
      }
    } else {
      setSelectParentNotice(item);
      setModal(type);
    }
  };

  /**
   * 공지사항 타입 전환 핸들러
   * 일반 공지와 서버 시스템 상태를 전환합니다.
   * @param {boolean} currentIsNormalNotice - 현재 일반 공지 여부
   * @returns {Function} 클릭 핸들러 함수
   */
  const handleClickSwitch = (currentIsNormalNotice: boolean) => () => {
    setIsNormalNotice(currentIsNormalNotice);
    setScrollData({
      page: 1,
      list: [],
      lastOfList: false,
      noResults: false,
    });
  };

  return (
    <ScreenLayout isLoading={isLoading} errorModal={errorModal}>
      <Container>
        <Inner>
          <Header>
            <SwitchModeWrap>
              {/* 서버 시스템 상태 */}
              <DefaultText
                text="서버 시스템 상태"
                textStyle={{
                  fontSize: minmax(30),
                  fontWeight: 'bold',
                  borderBottom: `3px solid ${
                    isNormalNotice ? 'transparent' : colors.primary
                  }`,
                  cursor: 'pointer',
                }}
                onClick={handleClickSwitch(false)}
              />
              {/* 일반 공지 */}
              <DefaultText
                text="일반 공지"
                textStyle={{
                  fontSize: minmax(30),
                  fontWeight: 'bold',
                  borderBottom: `3px solid ${
                    isNormalNotice ? colors.primary : 'transparent'
                  }`,
                  cursor: 'pointer',
                }}
                onClick={handleClickSwitch(true)}
              />
            </SwitchModeWrap>

            {/* 등록 버튼 */}
            <DefaultButton
              text="register"
              actived={1}
              buttonStyle={{
                width: minmax(100),
                height: minmax(50),
                backgroundColor: colors.primary2,
              }}
              onClick={() => setModal('register')}
            />
          </Header>

          <ContentList ref={listRef}>
            {noticeData?.totalCount
              ? noticeData.statuses.map(item => (
                  <ContentItem key={item.uuid}>
                    <NoticeItem
                      data={item}
                      onClickButton={(item, type) =>
                        handleClickButtons(item, type)
                      }
                    />
                    {/* {item.childStatusHistoryList
                      ? item.childStatusHistoryList.length !== 0 &&
                        item.childStatusHistoryList.map((childItem: any) => (
                          <NoticeItem
                            key={childItem.uuid}
                            data={childItem}
                            isChildren
                            onClickButton={(item, type) =>
                              handleClickButtons(item, type)
                            }
                          />
                        ))
                      : null} */}
                  </ContentItem>
                ))
              : null}

            {noticeData?.statuses.length !== 0 && !scrollData.lastOfList && (
              <div ref={setObserverRef} style={{height: '0.1px', opacity: 0}}>
                endOfList
              </div>
            )}
          </ContentList>
        </Inner>
      </Container>

      {/* 등록 혹은 수정 | 그 외 */}

      {modal !== '' ? (
        modal === 'register' || modal === 'modify' ? (
          <RegisterNoticeModal
            closeModal={() => {
              setSelectParentNotice(null);
              setModal('');
            }}
            setIsLoading={setIsLoading}
            setErrorModal={setErrorModal}
            parentData={selectParentNotice}
            isModify={modal === 'modify'}
            isNormalNotice={isNormalNotice}
          />
        ) : (
          <CommonModal
            title={modalData[modal].title}
            detail={modalData[modal].detail}
            firstButtonText={modalData[modal].firstButtonText}
            firstButtonStyle={modalData[modal].firstButtonStyle}
            secondButtonText={modalData[modal].secondButtonText}
            secondButtonStyle={modalData[modal].secondButtonStyle}
            firstButtonClick={modalData[modal].firstButtonClick}
            secondButtonClick={modalData[modal].secondButtonClick}
            closeModal={
              modalData[modal].secondButtonStyle ||
              modalData[modal].firstButtonClick
            }
          />
        )
      ) : null}
    </ScreenLayout>
  );
};

export default Notice;

const Container = styled.div`
  display: flex;
  width: 100%;
  flex: 1;
  flex-direction: column;
  margin-bottom: ${minmax(50)};
`;

const Inner = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: ${minmax(20)};
`;

const Header = styled.div`
  width: 100%;
  margin-bottom: ${minmax(40)};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const SwitchModeWrap = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  column-gap: ${minmax(20)};
`;

const ContentList = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${minmax(20)};
`;

const ContentItem = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  row-gap: ${minmax(10)};
`;
